import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { SlideOut } from '~/chords/SlideOut';
import { Icon, Separator } from '~/components';
import { Button } from '~/components/Button';
import { Modal } from '~/components/Modal';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { APIError } from '../../api/errors';
import {
  getFixesHistory,
  getLabelPerfReport,
  getLables,
  getMonthlyContrib,
} from '../../api/label-perf';
import { getLabelHierarchy } from '../../api/labels';
import type { AppliedLabelFilter } from '../../components/AppliedFilter/AppliedFilterList';
import { AppliedFilterList } from '../../components/AppliedFilter/AppliedFilterList';
import { Header } from '../../components/Header';
import { getFilteredLabelItems } from '../../components/LabelFilter/helper';
import { LabelFilter } from '../../components/LabelFilter/LabelFilter';
import { PageLoader } from '../../components/PageLoader';
import { retryOn504Options } from '../../constants/api';
import {
  CHART_TABS,
  DEFAULT_CHART_RANGE,
  DEFAULT_LABELS_FILTERS,
} from '../../constants/label-perf';
import { useAmplifyUser } from '../../context/useAmplifyUser';
import type { LabelData } from '../../types';
import {
  type FixesHistValue,
  type HistRange,
  type LabelPerfFilters,
  type LabelPerfModel,
  type MonthlyContrib,
} from '../../types/label-perf';
import { formatNumberSuffix } from '../../utils/format';
import { getLegendItems } from '../../utils/hist';
import { calculateTrendPercentage, getTrendDirection } from '../../utils/trend';
import {
  Card,
  Chart,
  ErrorDisplay,
  LegendBar,
  LegendList,
  SearchBar,
  Tabs,
  Title,
} from './components';
import { FilterButton } from './components/FilterButton/FilterButton';
import { FilterPanel } from './components/FilterPanel/FilterPanel';
import { NoAccess } from './components/NoAccess';
import { SelectedLabelsCounter } from './components/SelectedLabelsCounter';
import { TrackPerformance } from './components/TrackPerformance/TrackPerformance';

const FIRST_TIME_VISIT_STORAGE_KEY = 'dsc-exec-view-visited';

export function LabelPerf() {
  const { user, isLoading } = useAmplifyUser();

  const dmdLabelsFlagFromUser =
    user?.feature_flags?.['dsc-exec-view-dmd-labels'];
  const dscExecViewOldLabelFilter =
    user?.feature_flags?.['dsc-exec-view-old-label-filter'];

  const isDscExecLabelHierarchyView =
    dmdLabelsFlagFromUser !== undefined
      ? !dmdLabelsFlagFromUser
      : localStorage.getItem('dsc-exec-view-dmd-labels') !== 'true';

  const mixOneEnrich =
    user?.feature_flags?.['dsc-exec-view-familyid-mixone'] ?? false;

  const { t } = useTranslation('label-perf');
  const { trackEvent } = useAnalytics('Label Performance');

  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState<boolean>(false);

  const [chartTab, setChartTab] = useState<HistRange>(DEFAULT_CHART_RANGE);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInputValue, setSearchInputValue] = useState('');

  const [labels, setLabels] = useState<string[]>(
    searchParams
      .get('labels')
      ?.split(',')
      ?.filter((l) => isNaN(+l)) || [],
  );

  const renderLoadingState = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <PageLoader />
    </div>
  );

  const updateSearchParams = useCallback(
    (selectedLabels: string[] | number[]) => {
      const params = new URLSearchParams('');

      if (selectedLabels && selectedLabels.length) {
        params.set('labels', selectedLabels.toString());
      }

      setSearchParams(params);
    },
    [setSearchParams],
  );

  // START OF Version-2 LABELS
  const [labelIds, setLabelIds] = useState<number[]>(
    searchParams
      .get('labels')
      ?.split(',')
      ?.map(Number)
      .filter((l) => !!l) || [],
  );

  // These are the full LabelData objects filtered by the current labelIds.
  const [filteredLabels, setFilteredLabels] = useState<LabelData[]>([]);

  const appliedFilterItems = useMemo<AppliedLabelFilter[]>(() => {
    return filteredLabels.map((label) => {
      const { labelId, name, updatedAt, updatedBy } = label;

      return {
        labelId,
        displayName: name,
        updatedAt,
        updatedBy,
      };
    });
  }, [filteredLabels]);

  const onSubmit: SubmitHandler<LabelPerfFilters> = useCallback(
    (formData) => {
      const selectedLabels = formData.labels.map((item) => item.labelId);

      setLabelIds(selectedLabels);
      updateSearchParams(selectedLabels);
      trackEvent('labelPerformanceLabelFilterApplied', {
        label_count: formData.labels.length,
        labels_selected: formData.labels.map((l) => l.name),
      });
    },
    [setLabelIds, updateSearchParams, trackEvent],
  );

  const handleClearFilters = useCallback(() => {
    setFilteredLabels(DEFAULT_LABELS_FILTERS.labels);
    setLabelIds([]);
    updateSearchParams([]);
  }, [updateSearchParams]);

  const handleRemoveLabelFilter = useCallback(
    (deletedLabelId: number) => {
      const newFilteredLabelData = filteredLabels.filter(
        ({ labelId }) => labelId !== deletedLabelId,
      );

      setFilteredLabels(newFilteredLabelData);
      setLabelIds(newFilteredLabelData.map((l) => l.labelId));
      updateSearchParams(newFilteredLabelData.map((l) => l.labelId));
    },
    [filteredLabels, updateSearchParams],
  );

  const currentFilterValues: LabelPerfFilters = useMemo(() => {
    return {
      labels: filteredLabels.map((label) => ({
        labelId: label.labelId,
        name: label.name,
      })),
      isCrossover: false,
      isOnRoster: false,
    };
  }, [filteredLabels]);

  const methods = useForm<LabelPerfFilters>({
    defaultValues: structuredClone(currentFilterValues),
  });

  const {
    handleSubmit,
    watch,
    formState: { isDirty },
    reset,
  } = methods;
  const watchedLabels = watch('labels');

  const { data: labelHierarchyData, isLoading: isLoadingLabelsHierarchy } =
    useQuery({
      queryKey: ['labels-hierarchies-label-perf'],
      queryFn: () => getLabelHierarchy(),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      enabled: isDscExecLabelHierarchyView,
    });

  const handleApplyOnClose = useCallback(() => {
    if (isDirty) {
      handleSubmit(onSubmit)();
    }

    setIsFilterPanelOpen(false);
  }, [isDirty, handleSubmit, onSubmit]);

  const isApplyButtonDisabled = useMemo(() => {
    // Disable if no labels are selected in the form
    if (!watchedLabels || watchedLabels.length === 0) {
      return true;
    }

    return !isDirty;
  }, [watchedLabels, isDirty]);

  const filteredLabelHierarchyItems = useMemo(
    () => labelHierarchyData || [],
    [labelHierarchyData],
  );

  // Update the filtered label data once the data loads.
  useEffect(() => {
    if (labelIds && labelHierarchyData) {
      const filteredData = getFilteredLabelItems(labelHierarchyData)
        .filter((item) => labelIds.includes(item.label_id))
        .map((item) => ({
          name: item.name,
          labelId: item.label_id,
        }));

      setFilteredLabels(filteredData);
    }
  }, [labelIds, labelHierarchyData]);

  const filteredTotalCount = useMemo(() => {
    if (!filteredLabelHierarchyItems.length) {
      return 0;
    }

    return getFilteredLabelItems(filteredLabelHierarchyItems).length;
  }, [filteredLabelHierarchyItems]);

  // END OF V2

  const {
    data: labelsData,
    isLoading: labesDataLoading,
    error: labelsError,
  } = useQuery({
    queryKey: ['labels', searchInputValue],
    queryFn: () => getLables('major_label', searchInputValue),
    enabled: !isDscExecLabelHierarchyView,
  });

  const initialLabelsCount = useRef<number | null>(null);

  const filtersMetadata = useMemo(() => {
    return {
      areFiltersApplied: appliedFilterItems.length > 0,
      filterCount: appliedFilterItems.length,
    };
  }, [appliedFilterItems]);

  /* 
    When displaying the total count of labels in the search bar, we want the number
    to be the initial number of labels that were found without filtering the results. 
  */
  useEffect(() => {
    if (
      searchInputValue === '' &&
      labelsData?.length !== undefined &&
      initialLabelsCount.current === null
    ) {
      initialLabelsCount.current = labelsData.length;
    }
  }, [labelsData?.length, searchInputValue]);

  useEffect(() => {
    const hasVisited = localStorage.getItem(FIRST_TIME_VISIT_STORAGE_KEY);

    if (!hasVisited) {
      const timer = setTimeout(() => {
        setIsInfoDialogOpen(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleOpenDialog = useCallback(() => {
    setIsInfoDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsInfoDialogOpen(false);
    // Set the flag in localStorage so the dialog doesn't open automatically on the next visit.
    localStorage.setItem(FIRST_TIME_VISIT_STORAGE_KEY, 'true');
  }, []);

  const {
    data: labelPerfData,
    isLoading: isLablelPerfDataLoading,
    isError,
  } = useQuery({
    queryKey: ['label-perf', labels, labelIds],
    queryFn: () => getLabelPerfReport(labels, labelIds),
    ...retryOn504Options,
  });

  const { data: monthlyContrib } = useQuery({
    queryKey: ['monthly-contrib', labels, labelIds],
    queryFn: () => getMonthlyContrib(labels, 5 * 12, labelIds),
    ...retryOn504Options,
  });

  const {
    data: chartData,
    isLoading: chartLoading,
    isError: chartError,
  } = useQuery({
    queryKey: ['fixes-history', labels, chartTab, labelIds],
    queryFn: ({ queryKey }) => {
      const [, , histRange] = queryKey;

      return getFixesHistory(labels, histRange as HistRange, labelIds);
    },
    ...retryOn504Options,
  });

  const onSearchInputClear = useCallback(() => {
    setSearchInputValue('');
  }, []);

  const onSubmitLabels = useCallback(
    (selectedLabels: string[]) => {
      setLabels(selectedLabels);
      updateSearchParams(selectedLabels);

      trackEvent('labelPerformanceLabelFilterApplied', {
        label_count: selectedLabels.length,
        labels_selected: selectedLabels,
      });
    },
    [updateSearchParams, trackEvent],
  );

  useEffect(() => {
    if (labelPerfData) {
      // Reset the form with the *current* filteredLabels to mark it as not dirty
      // with respect to the currently displayed data.
      reset({
        labels: filteredLabels.map((label) => ({
          labelId: label.labelId,
          name: label.name,
        })),
        isCrossover: false,
        isOnRoster: false,
      });
    }
  }, [labelPerfData, filteredLabels, reset]);

  // Revenue is trimmed when the API returns null/undefined for revenue values
  const isRevenueTrimmed = (data: LabelPerfModel): boolean =>
    data.overallMonthlyRevenue == null;

  const renderPerformanceCards = (data: LabelPerfModel) => {
    const showRevenue = !isRevenueTrimmed(data);

    return (
      <div
        className={
          showRevenue ? 'grid grid-cols-3 gap-md' : 'grid grid-cols-2 gap-md'
        }
      >
        <Card
          value={formatNumberSuffix(data.totalAssets)}
          tooltipContent={t('tooltips.totalAssets')}
          title={t('body.totalAssets')}
          iconType="music"
          contentClassName="border-r border-emphasis-subtle"
          valueTestId="totalAssets"
        />

        <Card
          value={formatNumberSuffix(data.overallMonthlyStreams)}
          tooltipContent={t('tooltips.overallMonthlyStreams')}
          title={t('body.overallMonthlyStreams')}
          iconType="chart-line"
          trendDirection={getTrendDirection(data.overallMonthlyStreamsChange)}
          trendValue={calculateTrendPercentage(
            data.overallMonthlyStreams,
            data.overallMonthlyStreamsChange,
          )}
          contentClassName={
            showRevenue ? 'border-r border-emphasis-subtle' : undefined
          }
          valueTestId="overallMonthlyStreams"
        />

        {showRevenue ? (
          <Card
            value={'$' + formatNumberSuffix(data.overallMonthlyRevenue!)}
            tooltipContent={t('tooltips.overallMonthlyRevenue')}
            title={t('body.overallMonthlyRevenue')}
            iconType="usd"
            trendDirection={getTrendDirection(
              data.overallMonthlyRevenueChange!,
            )}
            trendValue={calculateTrendPercentage(
              data.overallMonthlyRevenue!,
              data.overallMonthlyRevenueChange!,
            )}
            valueTestId="overallMonthlyRevenue"
          />
        ) : null}
      </div>
    );
  };

  const renderOpportunityCards = (
    data: LabelPerfModel,
    monthlyContributions: MonthlyContrib[],
  ) => {
    const showRevenue = !isRevenueTrimmed(data);

    if (showRevenue) {
      return (
        <div className="grid grid-cols-3">
          <Card
            value={
              <>
                <span>
                  {'$' + formatNumberSuffix(data.opportunityRevenueFromFixes!)}
                  <span className="text-display-lg font-normal text-subtle">
                    /{t('date.year')}
                  </span>
                </span>
              </>
            }
            tooltipContent={t('tooltips.opportunityRevenueFromFixes')}
            title={t('body.yearlyRevenueOpportunity')}
            iconType="usd"
            className="row-span-2"
            contentClassName="justify-between border-r border-emphasis-subtle"
            valueTestId="yearlyRevenueOpportunity"
          />

          <Card
            value={
              monthlyContributions.length
                ? formatNumberSuffix(
                    monthlyContributions.reduce(
                      (r, v) => r + v.totalStreams,
                      0,
                    ),
                    0,
                  )
                : '--'
            }
            tooltipContent={t('tooltips.allStreamsFromFixes')}
            title={t('body.allStreamsFromFixes')}
            iconType="chart-line"
            contentClassName="border-r border-emphasis-subtle"
            className="border-b border-emphasis-subtle"
            valueTestId="allStreamsFromFixes"
          />

          <Card
            value={
              monthlyContributions.length
                ? `$${formatNumberSuffix(
                    monthlyContributions.reduce(
                      (r, v) => r + (v.totalRevenue ?? 0),
                      0,
                    ),
                    0,
                  )}`
                : '--'
            }
            tooltipContent={t('tooltips.allRevenueFromFixes')}
            title={t('body.allRevenueFromFixes')}
            iconType="usd"
            className="border-b border-emphasis-subtle"
            valueTestId="allRevenueFromFixes"
          />

          <Card
            value={formatNumberSuffix(data.streamsFromFixes)}
            tooltipContent={t('tooltips.monthlyStreamsIncrement')}
            title={t('body.monthlyStreamsIncrement')}
            iconType="chart-line"
            trendDirection={getTrendDirection(data.streamsFromFixesChange)}
            trendValue={calculateTrendPercentage(
              data.streamsFromFixes,
              data.streamsFromFixesChange,
            )}
            contentClassName="border-r border-emphasis-subtle"
            valueTestId="monthlyStreamsIncrement"
          />

          <Card
            value={'$' + formatNumberSuffix(data.revenueFromFixes!)}
            tooltipContent={t('tooltips.monthlyRevenueIncrement')}
            title={t('body.monthlyRevenueIncrement')}
            iconType="dollar-sign"
            trendDirection={getTrendDirection(data.revenueFromFixesChange!)}
            trendValue={calculateTrendPercentage(
              data.revenueFromFixes!,
              data.revenueFromFixesChange!,
            )}
            valueTestId="monthlyRevenueIncrement"
          />
        </div>
      );
    }

    // Revenue is trimmed - show only stream cards
    return (
      <div className="grid grid-cols-2">
        <Card
          value={
            monthlyContributions.length
              ? formatNumberSuffix(
                  monthlyContributions.reduce((r, v) => r + v.totalStreams, 0),
                  0,
                )
              : '--'
          }
          tooltipContent={t('tooltips.allStreamsFromFixes')}
          title={t('body.allStreamsFromFixes')}
          iconType="chart-line"
          contentClassName="border-r border-emphasis-subtle"
          className="border-b border-emphasis-subtle"
          valueTestId="allStreamsFromFixes"
        />

        <Card
          value={formatNumberSuffix(data.streamsFromFixes)}
          tooltipContent={t('tooltips.monthlyStreamsIncrement')}
          title={t('body.monthlyStreamsIncrement')}
          iconType="chart-line"
          trendDirection={getTrendDirection(data.streamsFromFixesChange)}
          trendValue={calculateTrendPercentage(
            data.streamsFromFixes,
            data.streamsFromFixesChange,
          )}
          className="border-b border-emphasis-subtle"
          valueTestId="monthlyStreamsIncrement"
        />
      </div>
    );
  };

  const renderChartsData = (
    data: LabelPerfModel,
    chartsData: FixesHistValue[] | undefined,
  ) => (
    <div className="mt-lg rounded-sm border px-2xl py-lg bg-level-02 border-emphasis-subtle">
      {/* HEADER */}
      <div className="mb-xl flex justify-between">
        <p className="text-body-sm font-bold">{t('body.needsReview')}</p>
        <Tabs
          defaultTab={chartTab}
          items={CHART_TABS}
          loading={chartLoading}
          onChange={(tabItem) => {
            const newChartTab = tabItem.id as HistRange;

            trackEvent(
              'labelPerformanceNeedsReviewVSAccuracyTableDateFilterChange',
              {
                previous_date_filter: chartTab,
                selected_date_filter: newChartTab,
              },
            );

            setChartTab(newChartTab);
          }}
          theme="toggler"
        />
      </div>
      {/* INFO */}
      <div className="mb-lg">
        <div>
          <div className="flex items-end justify-between">
            <div>
              <p className="flex items-center">
                <span className="text-display-lg">
                  {formatNumberSuffix(data.totalGaps)}
                </span>
                <span className="ml-sm mr-sm text-display-sm text-subtle">
                  {Number.isFinite(data.totalGaps / data.totalAssets)
                    ? `(${((data.totalGaps / data.totalAssets) * 100).toFixed(2)}%)`
                    : 'N/A'}
                </span>
                <span className="text-display-sm text-subtle">
                  {t('body.ofTotalAssets', {
                    totalAssets: formatNumberSuffix(data.totalAssets),
                  })}
                </span>
              </p>
            </div>
            <div className="ml-auto">
              {chartsData ? (
                <LegendList items={getLegendItems(chartData)} />
              ) : null}
            </div>
          </div>
          {chartsData ? (
            <LegendBar className="mt-md" items={getLegendItems(chartData)} />
          ) : null}
        </div>
        <div />
      </div>
      {/* CHART CANVAS */}
      <div className="relative">
        <Chart
          data={chartsData || []}
          isLoading={chartLoading}
          isError={chartError}
        />
      </div>
    </div>
  );

  const isForbidden =
    labelsError instanceof APIError && labelsError.status === 403;

  const noAccess =
    // No search input, no labels to display, and loading is complete
    (!searchInputValue && labelsData?.length === 0 && !labesDataLoading) ||
    // The labels are forbidden
    isForbidden ||
    // The user is not active
    (!isLoading && user && !user?.is_active) ||
    // The user role is 'user' but they have no major labels
    (user?.role === 'user' &&
      (user.major_labels?.length === 0 || !user?.major_labels));

  if (noAccess) {
    return <NoAccess />;
  }

  return (
    <>
      <Helmet>
        <title>{t('header.title')}</title>
      </Helmet>
      <div className="p-xl">
        {!isLablelPerfDataLoading && (
          <>
            <div className="flex items-center">
              <Header />
              <Button
                variant="text"
                className="ml-sm mt-xs text-body-xs"
                onClick={() => handleOpenDialog()}
              >
                <Icon
                  className="cursor-pointer"
                  type="circle-info"
                  variant="light"
                  size="xs"
                />
              </Button>

              <Button
                variant="secondary"
                className="ml-md py-sm font-bold shadow-md outline outline-1 bg-emphasis-default outline-emphasis-subtle"
              >
                <a
                  className="flex items-center text-body-xs"
                  target="_blank"
                  href="https://docs.google.com/forms/d/e/1FAIpQLSefQ1_QjxKfrYjKxX0fDwgOr8iwGy8ZH0wIAO6Gz6HlU0YvJw/viewform"
                >
                  <Icon
                    className="mr-sm cursor-pointer"
                    type="comment"
                    variant="regular"
                    size="xs"
                  />
                  {t('actions.feedback')}
                </a>
              </Button>
            </div>

            <Separator className="mb-lg mt-md" />
          </>
        )}

        {user ? (
          <>
            <div className="min-w-[1024px]">
              {!isDscExecLabelHierarchyView ? (
                <div>
                  <Title className="mb-sm text-body-md text-default">
                    {t('body.majorLabels')}
                  </Title>
                  <SearchBar
                    data={labelsData || []}
                    totalLabelsCount={initialLabelsCount.current || 0}
                    isLoading={labesDataLoading}
                    inputValue={searchInputValue}
                    selectedValues={labels}
                    placeholder={t('body.searchLabels')}
                    onSubmit={onSubmitLabels}
                    onChange={setSearchInputValue}
                    onClear={onSearchInputClear}
                    aria-label="Search labels by label name"
                  />
                </div>
              ) : null}

              {isLablelPerfDataLoading ? (
                renderLoadingState()
              ) : (
                <>
                  {isDscExecLabelHierarchyView && !dscExecViewOldLabelFilter ? (
                    <>
                      <section className="mb-2xl flex items-center justify-between">
                        {!labelIds.length ? (
                          <p>
                            {t('body.filtersApplied', { value: 'All labels' })}
                          </p>
                        ) : null}
                        {labelIds.length ? (
                          <AppliedFilterList
                            isCrossover={false}
                            labelFilters={appliedFilterItems}
                            onRemoveLabel={handleRemoveLabelFilter}
                            onClear={handleClearFilters}
                          />
                        ) : null}
                        <FilterButton
                          filtersMetadata={filtersMetadata}
                          onShowFilters={() => setIsFilterPanelOpen(true)}
                        />
                      </section>
                    </>
                  ) : null}

                  {isDscExecLabelHierarchyView && dscExecViewOldLabelFilter ? (
                    <section className="mb-2xl flex flex-col gap-sm">
                      <FormProvider {...methods}>
                        <form
                          onSubmit={handleSubmit(onSubmit)}
                          className="flex basis-full flex-col"
                        >
                          <div className="mb-sm flex justify-between">
                            <p className="font-semibold">{t('body.labels')}</p>
                            {labelHierarchyData ? (
                              <SelectedLabelsCounter
                                selectedCount={labelIds.length}
                                totalCount={filteredTotalCount}
                                label={t('body.selected')}
                              />
                            ) : null}
                          </div>
                          <div className="flex gap-md">
                            <LabelFilter
                              originalLabels={currentFilterValues.labels}
                              labelHierarchyData={filteredLabelHierarchyItems}
                              isLoading={isLoadingLabelsHierarchy}
                              disabled={isLablelPerfDataLoading}
                              onApply={handleApplyOnClose}
                            />
                            <Button
                              variant="primary"
                              disabled={
                                isApplyButtonDisabled || isLablelPerfDataLoading
                              }
                              size="large"
                              type="submit"
                            >
                              {t('actions.apply')}
                            </Button>
                          </div>
                        </form>
                      </FormProvider>

                      {labelIds.length ? (
                        <AppliedFilterList
                          isCrossover={false}
                          labelFilters={appliedFilterItems}
                          onRemoveLabel={handleRemoveLabelFilter}
                          onClear={handleClearFilters}
                        />
                      ) : null}
                    </section>
                  ) : null}
                </>
              )}
              {isError && !labesDataLoading ? (
                <ErrorDisplay
                  title={t('error.failedLoading')}
                  description={t('error.refresh')}
                  className="min-h-[20em]"
                />
              ) : null}

              {labelPerfData ? (
                <div>
                  {/* Header Section */}
                  <div className="mb-xs">
                    <Title className="text-body-md text-default">
                      {t('body.overallPerformance')}
                    </Title>
                  </div>

                  {/* Performance Cards */}
                  <div className="mb-2xl overflow-hidden">
                    <Separator />
                    {renderPerformanceCards(labelPerfData)}
                    <Separator />
                  </div>

                  {/* TRACK PERFORMANCE */}
                  <TrackPerformance
                    key={labelIds.join(',')}
                    majorLabels={labels}
                    wmg1Labels={labelIds}
                    mixOneEnrich={mixOneEnrich}
                  />

                  {/* Opportunity Section */}
                  <div className="mb-md mt-2xl">
                    <Title className="mb-xs text-body-md text-default">
                      {t('body.territoryDSPRevenueOpportunities')}
                    </Title>
                    <p className="max-w-[408px] text-body-sm">
                      {t('body.territoryDSPRevenueOpportunitiesDesc')}
                    </p>
                  </div>

                  <div className="overflow-hidden">
                    <Separator />
                    {renderOpportunityCards(
                      labelPerfData,
                      monthlyContrib || [],
                    )}
                  </div>

                  {/* CHART */}
                  {renderChartsData(labelPerfData, chartData)}
                </div>
              ) : null}
            </div>

            <SlideOut
              open={isFilterPanelOpen}
              onOpenChange={setIsFilterPanelOpen}
            >
              <FilterPanel
                originalLabels={currentFilterValues.labels}
                labelHierarchyData={filteredLabelHierarchyItems}
                isLoading={isLoadingLabelsHierarchy}
                disabled={isLablelPerfDataLoading}
                onApplyFilters={onSubmit}
                onClose={() => setIsFilterPanelOpen(false)}
              />
            </SlideOut>

            <Modal isOpen={isInfoDialogOpen} onClose={handleCloseDialog}>
              <div className="w-[44em] max-w-full rounded-md p-lg shadow-lg bg-level-02">
                <div className="mb-md flex items-center justify-between align-middle">
                  <div className="flex items-center gap-sm align-middle text-white">
                    <Icon type="chart-line" variant="regular" size="lg" />
                    <span className="ml-sm text-headline-md font-semibold">
                      {t('header.title')}
                    </span>
                  </div>
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      handleCloseDialog();
                    }}
                    className="inline-flex size-sm justify-center rounded-sm p-md duration-300 ease-out text-white hover:bg-secondary-hover"
                  >
                    <Icon type="xmark" variant="regular" size="lg" />
                  </button>
                </div>
                <div className="mb-lg">
                  <p className="mb-md mr-2xl text-subtle">
                    {t('desc.labelPerfFirstVisit')}
                  </p>
                  <img
                    className="h-[116px] w-full"
                    src="/images/label-perf-info.png"
                    alt={t('header.title')}
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    className="text-body-md font-semibold"
                    onClick={() => handleCloseDialog()}
                  >
                    {t('actions.ok')}
                  </Button>
                </div>
              </div>
            </Modal>
          </>
        ) : (
          renderLoadingState()
        )}
      </div>
    </>
  );
}

LabelPerf.displayName = 'LabelPerf';
